import { languageConfigurations, defaultLanguageCode } from './index';
import { rtlLangs } from './rtl-langs';

// Resolve a locale to a known configuration using a fallback chain:
// 1. Exact match (e.g. "fr-BE" -> "fr-BE")
// 2. Base language match (e.g. "fr-BE" -> first "fr-*" config)
// 3. Default locale (en-US)
export const resolveLocale = (locale?: string | null): string => {
  if (!locale) return defaultLanguageCode;

  const exact = languageConfigurations.find((config) => config.code === locale);
  if (exact) return exact.code;

  const baseLang = (locale.split('-')[0] ?? '').toLowerCase();
  if (!baseLang) return defaultLanguageCode;

  const baseMatch = languageConfigurations.find(
    (config) => config.code.split('-')[0].toLowerCase() === baseLang
  );
  if (baseMatch) return baseMatch.code;

  return defaultLanguageCode;
};

export const getIntlMessages = (locale: string) => {
  const resolved = resolveLocale(locale);
  const langConfig = languageConfigurations.find(
    (config) => config.code === resolved
  );
  return langConfig?.translation || {};
};

export const getAvailableLocales = () => {
  return languageConfigurations.map((config) => config.code);
};

export const getDefaultLocale = () => {
  return defaultLanguageCode;
};

const flattenMessages = (
  nestedMessages: any,
  prefix = ''
): Record<string, string> => {
  let messages: Record<string, string> = {};

  for (const key in nestedMessages) {
    const value = nestedMessages[key];
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'string') {
      messages[newKey] = value;
    } else if (typeof value === 'object' && value !== null) {
      messages = { ...messages, ...flattenMessages(value, newKey) };
    }
  }

  return messages;
};

export const getFlattenedMessages = (locale: string) => {
  const messages = getIntlMessages(locale);
  return flattenMessages(messages);
};

export const isRTLLocale = (locale: string): boolean => {
  return rtlLangs.includes(locale);
};

export const getRTLLocales = (): string[] => {
  return [...rtlLangs];
};

export const initializeIntl = () => {
  return {
    defaultLocale: getDefaultLocale(),
    availableLocales: getAvailableLocales(),
    messages: getFlattenedMessages(getDefaultLocale()),
    rtlLocales: getRTLLocales(),
  };
};

export const initializeReactIntl = () => {
  const intlLanguages = languageConfigurations.map((config) => ({
    name: config.name,
    code: config.code,
    messages: flattenMessages(config.translation),
    isRTL: isRTLLocale(config.code),
  }));

  return {
    languages: intlLanguages,
    defaultLanguage: defaultLanguageCode,
  };
};
